import type { Dispatch, FC, SetStateAction } from "react";

import { Box, Typography, useTheme, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { BrandingNotificationSize } from "./BrandingNotificationSize";
import CustomSwitch from "../../../components/CustomSwitch";
import { BrandingIcons } from "./BrandingIcons";
import { BrandingPreview } from "./BrandingPreview";
import { BrandingEditNotification } from "../../../components/NotificationEditComponents/BrandingEditNotification";
import { BrandingDefaultSettings } from "./BrandingDefaultSettings";
import { useTranslation } from "react-i18next";
import { useEditCampaign } from "../../../context/EditCampaignContext";

interface BrandingProps {
  setModalOpen: Dispatch<SetStateAction<boolean>>;
  showIcon: boolean;
  textColor: string | undefined;
  backgroundColor: string | undefined;
  borderRadius: number | undefined;
  backgroundOpacity: number | undefined;
  iconColor: string | undefined;
  iconBackgroundColor: string | undefined;
  iconBackgroundRadius: number | undefined;
  modalOpen: boolean;
  activeSize: "classic" | "small" | "compact";
  setActiveSize: Dispatch<SetStateAction<"classic" | "small" | "compact">>;
  setTextColor: Dispatch<SetStateAction<string | undefined>>;
  setBackgroundColor: Dispatch<SetStateAction<string | undefined>>;
  setBorderRadius: Dispatch<SetStateAction<number | undefined>>;
  setBackgroundOpacity: Dispatch<SetStateAction<number | undefined>>;
  setShowIcon: Dispatch<SetStateAction<boolean>>;
  setIconBackgroundColor: Dispatch<SetStateAction<string | undefined>>;
  setIconBackgroundRadius: Dispatch<SetStateAction<number | undefined>>;
  setIconColor: Dispatch<SetStateAction<string | undefined>>;
}

export const Branding: FC<BrandingProps> = ({
  setModalOpen,
  activeSize,
  backgroundColor,
  backgroundOpacity,
  borderRadius,
  iconBackgroundColor,
  iconBackgroundRadius,
  iconColor,
  showIcon,
  textColor,
  modalOpen,
  setActiveSize,
  setBackgroundColor,
  setBackgroundOpacity,
  setBorderRadius,
  setTextColor,
  setIconBackgroundColor,
  setIconBackgroundRadius,
  setShowIcon,
  setIconColor,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { setDetectedChange } = useEditCampaign();

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 5, pb: 10 }}>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              height: 300,
              backgroundColor: theme.palette.natural.t9,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <Box
              sx={{
                width: 130,
                height: 80,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "absolute",
                top: 0,
                left: 0,
              }}
            >
              <Button variant='outlined' onClick={() => setModalOpen(true)}>
                {t("campaign.preview")}
              </Button>
            </Box>
            <Box
              style={{
                height: 160,
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <BrandingEditNotification
                icon={showIcon}
                textColor={textColor}
                backgroundColor={backgroundColor}
                borderRadius={borderRadius}
                backgroundOpacity={backgroundOpacity}
                iconColor={iconColor}
                iconBackgroundColor={iconBackgroundColor}
                iconBackgroundRadius={iconBackgroundRadius}
                activeSize={activeSize}
              />
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Typography
            sx={{
              color: theme.palette.natural.t,
              fontWeight: "bold",
              fontSize: 20,
            }}
          >
            {t("campaign.branding.sizeAndForm.title")}
          </Typography>
          <BrandingNotificationSize
            setActive={setActiveSize}
            activeSize={activeSize}
          />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Typography
            sx={{
              color: theme.palette.natural.t,
              fontWeight: "bold",
              fontSize: 20,
            }}
          >
            {t("campaign.branding.defaultSettings.title")}
          </Typography>
          <BrandingDefaultSettings
            onTextColor={setTextColor}
            onBackgroundColor={setBackgroundColor}
            onBorderRadius={setBorderRadius}
            onBackgroundOpacity={setBackgroundOpacity}
            textColor={textColor}
            backgroundColor={backgroundColor}
            backgroundOpacity={backgroundOpacity}
            borderRadius={borderRadius}
          />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                color: theme.palette.natural.t,
                fontWeight: "bold",
                fontSize: 20,
              }}
            >
              {t("campaign.branding.icons.title")}
            </Typography>
            <CustomSwitch
              checked={showIcon}
              onSwitch={() => {
                setShowIcon(prev => !prev);
                setIconBackgroundColor(undefined);
                setIconBackgroundRadius(undefined);
                setIconColor(undefined);
                setDetectedChange(true);
              }}
            />
          </Box>
          {showIcon && (
            <BrandingIcons
              onIconColor={setIconColor}
              onIconBackgroundColor={setIconBackgroundColor}
              onIconBackgroundRadius={setIconBackgroundRadius}
              backgroundColor={iconBackgroundColor}
              backgroundRadius={iconBackgroundRadius}
              iconColor={iconColor}
            />
          )}
        </Box>
      </Box>
      <BrandingPreview modalOpen={modalOpen} setModalOpen={setModalOpen}>
        <CloseIcon
          sx={{
            position: "absolute",
            top: 10,
            right: 20,
            cursor: "pointer",
          }}
          onClick={() => setModalOpen(false)}
        />
        <Box
          style={{
            height: 160,
            position: "absolute",
            bottom: 0,
            left: 15,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <BrandingEditNotification
            icon={showIcon}
            textColor={textColor}
            backgroundColor={backgroundColor}
            borderRadius={borderRadius}
            backgroundOpacity={backgroundOpacity}
            iconColor={iconColor}
            iconBackgroundColor={iconBackgroundColor}
            iconBackgroundRadius={iconBackgroundRadius}
            activeSize={activeSize}
          />
        </Box>
      </BrandingPreview>
    </>
  );
};
